/**
 * Historical Events - Betrieb Müller (v2)
 *
 * Events from the past showing completed actions and resolved situations.
 * These appear in the "Earlier" (Früher) section.
 */

import type { MockControlEvent } from '@hofsync/design-system';
import { daysAgo } from '../farm-profile';

/**
 * Event 15 (EARLIER): TÜV Schlepper bestanden
 */
export const EVENT_HIST_TUEV_SCHLEPPER: MockControlEvent = {
  id: 'evt-hist-tuev-schlepper',
  type: 'situation-resolved',
  priority: 'success',
  title: 'TÜV bestanden: John Deere 6130R',
  subtitle: 'Fahrzeugprüfung',
  timestamp: daysAgo(60),
  relevantDate: daysAgo(60),
  relatedEntity: { name: 'John Deere 6130R', href: '#equipment-jd6130r' },
  categories: ['tuev'],
  controlType: 'TÜV',
  controlReadiness: {
    status: 'ready',
    metCount: 1,
    totalCount: 1,
    label: 'Bestanden',
  },

  // === TWO-COLUMN LAYOUT ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'equipment-jd6130r',
      name: 'John Deere 6130R',
      type: 'equipment',
      severity: 'info',
      status: 'TÜV bis November 2027',
      action: { label: 'Fahrzeug anzeigen', href: '/my-farm/equipment/jd6130r' },
    },
  ],
  domainLink: {
    label: 'Alle Fahrzeuge anzeigen',
    href: '/my-farm/equipment',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Hauptuntersuchung ohne Mängel bestanden.',
    legalBasis: [],
    factors: [
      { label: 'Prüfdatum', value: '27.11.2025' },
      { label: 'Ergebnis', value: 'Ohne Mängel' },
      { label: 'Nächste HU', value: 'November 2027' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * Event 16 (EARLIER): Stoffstrombilanz 2024 eingereicht
 */
export const EVENT_HIST_STOFFSTROM_2024: MockControlEvent = {
  id: 'evt-hist-stoffstrom-2024',
  type: 'situation-resolved',
  priority: 'success',
  title: 'Stoffstrombilanz 2024 eingereicht',
  subtitle: 'Nährstoffbilanz',
  timestamp: daysAgo(89),
  relevantDate: daysAgo(89),
  relatedEntity: { name: 'Stoffstrombilanz 2024', href: '#stoffstrom-2024' },
  categories: ['duev'],
  controlType: 'Stoffstrombilanz',
  controlReadiness: {
    status: 'ready',
    metCount: 1,
    totalCount: 1,
    label: 'Eingereicht',
  },

  // === TWO-COLUMN LAYOUT ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'doc-ssb-2024',
      name: 'Stoffstrombilanz 2024',
      type: 'document',
      severity: 'info',
      status: 'Eingereicht am 28.10.2025',
      action: { label: 'Dokument anzeigen', href: '/my-farm/documents/stoffstrombilanz-2024' },
    },
  ],
  domainLink: {
    label: 'Alle Bilanzen anzeigen',
    href: '/my-farm/balances',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Stoffstrombilanz 2024 fristgerecht eingereicht.',
    legalBasis: [],
    factors: [
      { label: 'Eingereicht am', value: '28.10.2025' },
      { label: 'Status', value: 'Bestätigt' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * Event 17 (EARLIER): Unterweisung Thomas Weber
 */
export const EVENT_HIST_UNTERWEISUNG_THOMAS: MockControlEvent = {
  id: 'evt-hist-unterweisung-thomas',
  type: 'situation-resolved',
  priority: 'success',
  title: 'Unterweisung: Thomas Weber',
  subtitle: 'Arbeitssicherheit',
  timestamp: daysAgo(21),
  relevantDate: daysAgo(21),
  relatedEntity: { name: 'Thomas Weber', href: '#person-thomas' },
  categories: ['unterweisungen'],
  controlType: 'Arbeitssicherheit',
  controlReadiness: {
    status: 'ready',
    metCount: 1,
    totalCount: 1,
    label: 'Dokumentiert',
  },

  // === TWO-COLUMN LAYOUT ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'person-thomas',
      name: 'Thomas Weber',
      type: 'person',
      severity: 'info',
      status: 'Unterwiesen am 05.01.2026',
      action: { label: 'Protokoll anzeigen', href: '/my-farm/employees/thomas' },
    },
  ],
  domainLink: {
    label: 'Alle Unterweisungen anzeigen',
    href: '/my-farm/safety',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Unterweisung durchgeführt und dokumentiert.',
    legalBasis: [],
    factors: [
      { label: 'Datum', value: '05.01.2026' },
      { label: 'Themen', value: 'PSA, Gefahrstoffe, Maschinensicherheit' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * Event 18 (EARLIER): DBE 2025 erstellt
 */
export const EVENT_HIST_DBE_2025: MockControlEvent = {
  id: 'evt-hist-dbe-2025',
  type: 'situation-resolved',
  priority: 'success',
  title: 'DBE 2025 erstellt',
  subtitle: 'Düngung',
  timestamp: daysAgo(330),
  relevantDate: daysAgo(330),
  relatedEntity: { name: 'DBE 2025', href: '#dbe-2025' },
  categories: ['duev'],
  controlType: 'Düngebedarfsermittlung',
  controlReadiness: {
    status: 'ready',
    metCount: 1,
    totalCount: 1,
    label: 'Vollständig',
  },

  // === TWO-COLUMN LAYOUT ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'doc-dbe-2025',
      name: 'Düngebedarfsermittlung 2025',
      type: 'document',
      severity: 'info',
      status: 'Erstellt am 01.03.2025',
      action: { label: 'Dokument anzeigen', href: '/my-farm/documents/dbe-2025' },
    },
  ],
  domainLink: {
    label: 'Düngeplanung anzeigen',
    href: '/my-farm/fertilization/planning',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'DBE wurde vor der ersten Düngungsmaßnahme für alle Schläge erstellt.',
    legalBasis: [],
    factors: [
      { label: 'Erstelldatum', value: '01.03.2025' },
      { label: 'Schläge', value: '4 von 4 berechnet' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * Event 19 (EARLIER): CC-Kontrolle 2023 ohne Beanstandungen
 */
export const EVENT_HIST_CC_KONTROLLE: MockControlEvent = {
  id: 'evt-hist-cc-kontrolle',
  type: 'situation-resolved',
  priority: 'success',
  title: 'CC-Kontrolle 2023: Ohne Beanstandungen',
  subtitle: 'Cross-Compliance',
  timestamp: daysAgo(660),
  relevantDate: daysAgo(660),
  relatedEntity: { name: 'Cross-Compliance 2023', href: '#cc-2023' },
  categories: ['cross-compliance'],
  controlType: 'Cross-Compliance',
  controlReadiness: {
    status: 'ready',
    metCount: 1,
    totalCount: 1,
    label: 'Bestanden',
  },

  // === TWO-COLUMN LAYOUT ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'control-cc-2023',
      name: 'CC-Kontrolle März 2023',
      type: 'inspection',
      severity: 'info',
      status: 'Ohne Beanstandungen',
      action: { label: 'Prüfbericht anzeigen', href: '/my-farm/compliance/cc-2023' },
    },
  ],
  domainLink: {
    label: 'Cross-Compliance anzeigen',
    href: '/my-farm/compliance',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Kontrolle ohne Beanstandungen bestanden.',
    legalBasis: [],
    factors: [
      { label: 'Kontrolldatum', value: 'März 2023' },
      { label: 'Kontrollbereiche', value: 'GAB 1-11, GLÖZ 1-9' },
      { label: 'Ergebnis', value: 'Ohne Beanstandungen' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * All historical events (EARLIER section)
 */
export const HISTORY_EVENTS: MockControlEvent[] = [
  EVENT_HIST_UNTERWEISUNG_THOMAS, // 3 weeks ago
  EVENT_HIST_TUEV_SCHLEPPER, // 2 months ago
  EVENT_HIST_STOFFSTROM_2024, // 3 months ago
  EVENT_HIST_DBE_2025, // 11 months ago
  EVENT_HIST_CC_KONTROLLE, // 22 months ago
];
